import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  TableContainer,
  TextField,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  Sync,
  Search as SearchIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";
import RowActionsMenu from "../../components/RowActionsMenu";
import MetricsStrip from "../../components/MetricsStrip";
import MetaConnectionModal from "../../components/MetaConnectionModal";
import {
  getConnectionDisconnectEndpoint,
  getConnectionDisplayStatus,
  getConnectionManagementActionTypes,
  isAwaitingWhatsAppQrCode,
  isWhatsAppChannel,
  shouldReauthorizeMeta,
} from "./connectionChannelState";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  importCard: {
    borderRadius: 18,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  rowName: {
    fontWeight: 600,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  sessionActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  sessionButton: {
    fontWeight: 600,
    borderRadius: 10,
    textTransform: "none",
    boxShadow: "none",
  },
  sessionButtonPrimary: {
    ...(theme.palette.type === "light"
      ? {
          backgroundColor: "#f472b6",
          color: "#5b1238",
          "&:hover": {
            backgroundColor: "#ec4899",
          },
        }
      : {
          backgroundColor: "#f9a8d4",
          color: "#4a044e",
          "&:hover": {
            backgroundColor: "#f472b6",
          },
        }),
  },
  sessionButtonOutlinedPrimary: {
    ...(theme.palette.type === "light"
      ? {
          color: "#9d174d",
          borderColor: "#f472b6",
          backgroundColor: "rgba(244, 114, 182, 0.12)",
          "&:hover": {
            borderColor: "#ec4899",
            backgroundColor: "rgba(244, 114, 182, 0.2)",
          },
        }
      : {
          color: "#fbcfe8",
          borderColor: "#f472b6",
          backgroundColor: "rgba(244, 114, 182, 0.18)",
          "&:hover": {
            borderColor: "#f9a8d4",
            backgroundColor: "rgba(244, 114, 182, 0.28)",
          },
        }),
  },
  sessionButtonOutlinedSecondary: {
    ...(theme.palette.type === "light"
      ? {
          color: "#831843",
          borderColor: "#ec4899",
          backgroundColor: "rgba(236, 72, 153, 0.1)",
          "&:hover": {
            borderColor: "#db2777",
            backgroundColor: "rgba(236, 72, 153, 0.18)",
          },
        }
      : {
          color: "#fce7f3",
          borderColor: "#f9a8d4",
          backgroundColor: "rgba(249, 168, 212, 0.16)",
          "&:hover": {
            borderColor: "#fbcfe8",
            backgroundColor: "rgba(249, 168, 212, 0.24)",
          },
        }),
  },
  sessionButtonDisabled: {
    ...(theme.palette.type === "light"
      ? {
          color: "#6b214d",
          borderColor: "#fbcfe8",
          backgroundColor: "rgba(251, 207, 232, 0.55)",
        }
      : {
          color: "#fce7f3",
          borderColor: "rgba(249, 168, 212, 0.5)",
          backgroundColor: "rgba(190, 24, 93, 0.22)",
        }),
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  mobileSection: {
    padding: theme.spacing(1.5),
  },
  mobileCards: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: theme.spacing(1.5),
  },
  mobileCard: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    boxShadow: "none",
  },
  mobileCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  mobileCardBody: {
    display: "grid",
    gap: theme.spacing(1),
  },
  mobileMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  mobileMetaLabel: {
    fontSize: "0.78rem",
    opacity: 0.65,
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const getChannelIcon = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998" }} />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c" }} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366" }} />;
    default:
      return null;
  }
};

const getStatusMeta = (status) => {
  switch (status) {
    case "CONNECTED":
      return {
        label: "Conectada",
        backgroundColor: "#dcfce7",
        color: "#166534",
      };
    case "qrcode":
      return {
        label: "Aguardando QR",
        backgroundColor: "#fef3c7",
        color: "#92400e",
      };
    case "OPENING":
      return {
        label: "Conectando",
        backgroundColor: "#e0f2fe",
        color: "#075985",
      };
    case "PAIRING":
    case "TIMEOUT":
      return {
        label: "Instavel",
        backgroundColor: "#fee2e2",
        color: "#991b1b",
      };
    default:
      return {
        label: "Desconectada",
        backgroundColor: "#fee2e2",
        color: "#991b1b",
      };
  }
};

const Connections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    channel: "whatsapp",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState,
  );
  const [planConfig, setPlanConfig] = useState(false);
  const [supportWhatsapp, setSupportWhatsapp] = useState("");
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [metaInitialChannel, setMetaInitialChannel] = useState("facebook");
  const [metaReconnectConnectionId, setMetaReconnectConnectionId] = useState(null);

  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const { getPlanCompany } = usePlans();

  const canManageConnections =
    user.profile === "admin" ||
    user.profile === "super" ||
    (user.profile === "user" && user.allowConnections === "enabled");

  useEffect(() => {
    if (!companyId) {
      return undefined;
    }

    let isMounted = true;

    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);

      if (isMounted) {
        setPlanConfig(planConfigs);
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [companyId, getPlanCompany]);

  const handleOpenMetaModal = (channel, connectionId = null) => {
    setMetaInitialChannel(channel);
    setMetaReconnectConnectionId(connectionId);
    setMetaModalOpen(true);
  };

  useEffect(() => {
    if (!user.companyId || !socket?.on || !socket?.off) {
      return undefined;
    }

    const handleImport = (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    };

    socket.on(`importMessages-${user.companyId}`, handleImport);

    return () => {
      socket.off(`importMessages-${user.companyId}`, handleImport);
    };
  }, [history, socket, user.companyId]);

  useEffect(() => {
    const fetchSupportWhatsapp = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");

        const fromPanel = data?.loginWhatsapp;
        const fromEnv = process.env.REACT_APP_NUMBER_SUPPORT
          ? `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`
          : "";

        setSupportWhatsapp(fromPanel || fromEnv);
      } catch (err) {
        if (process.env.REACT_APP_NUMBER_SUPPORT) {
          setSupportWhatsapp(
            `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`,
          );
        }
      }
    };

    fetchSupportWhatsapp();
  }, []);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const getEditConnectionAction = (whatsApp) => ({
    key: "edit",
    label: "Editar conexao",
    icon: Edit,
    onClick: () => handleEditWhatsApp(whatsApp),
  });

  const getReauthorizeMetaAction = (whatsApp) => ({
    key: "reauthorize",
    label: "Reautorizar Meta",
    icon: Sync,
    onClick: () => handleOpenMetaModal(whatsApp.channel, whatsApp.id),
  });

  const getDeleteConnectionAction = (whatsApp) => ({
    key: "delete",
    label: "Excluir conexao",
    icon: DeleteOutline,
    onClick: () => handleOpenConfirmationModal("delete", whatsApp.id),
  });

  const getConnectionRowActions = (whatsApp) => {
    const actionFactories = {
      edit: () => getEditConnectionAction(whatsApp),
      reauthorize: () => getReauthorizeMetaAction(whatsApp),
      delete: () => getDeleteConnectionAction(whatsApp),
    };

    return getConnectionManagementActionTypes(whatsApp).map((actionType) =>
      actionFactories[actionType](),
    );
  };

  const openInNewTab = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenConfirmationModal = (
    action,
    whatsAppId,
    channel = "whatsapp",
  ) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId,
        channel,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(
          getConnectionDisconnectEndpoint({
            id: confirmModalInfo.whatsAppId,
            channel: confirmModalInfo.channel,
          }),
        );
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
    setConfirmModalOpen(false);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          className={`${classes.sessionButton} ${classes.sessionButtonOutlinedPrimary}`}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      const isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime(),
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages),
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              className={`${classes.sessionButton} ${classes.sessionButtonOutlinedPrimary} ${classes.sessionButtonDisabled}`}
              size="small"
              endIcon={
                <CircularProgress
                  size={12}
                  className={classes.buttonProgress}
                />
              }
              variant="outlined"
              color="primary"
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }

    return null;
  };

  const renderActionButtons = (whatsApp) => {
    const displayStatus = getConnectionDisplayStatus(whatsApp);
    const whatsappChannel = isWhatsAppChannel(whatsApp.channel);

    return (
      <>
        {isAwaitingWhatsAppQrCode(whatsApp) && (
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="connections-page:addConnection"
            yes={() => (
              <div className={classes.sessionActions}>
                <Button
                  className={`${classes.sessionButton} ${classes.sessionButtonPrimary}`}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenQrModal(whatsApp)}
                >
                  {i18n.t("connections.buttons.qrcode")}
                </Button>
              </div>
            )}
          />
        )}
        {displayStatus === "DISCONNECTED" && whatsappChannel && (
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="connections-page:addConnection"
            yes={() => (
              <div className={classes.sessionActions}>
                <Button
                  className={`${classes.sessionButton} ${classes.sessionButtonOutlinedPrimary}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.tryAgain")}
                </Button>{" "}
                <Button
                  className={`${classes.sessionButton} ${classes.sessionButtonOutlinedSecondary}`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.newQr")}
                </Button>
              </div>
            )}
          />
          )}
        {shouldReauthorizeMeta(whatsApp) && (
            <Button
              className={`${classes.sessionButton} ${classes.sessionButtonOutlinedPrimary}`}
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleOpenMetaModal(whatsApp.channel, whatsApp.id)}
            >
              Reautorizar Meta
            </Button>
          )}
        {(displayStatus === "CONNECTED" ||
          (whatsappChannel &&
            ["PAIRING", "TIMEOUT"].includes(displayStatus))) && (
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <div className={classes.sessionActions}>
                <Button
                  className={`${classes.sessionButton} ${classes.sessionButtonOutlinedSecondary}`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    handleOpenConfirmationModal(
                      "disconnect",
                      whatsApp.id,
                      whatsApp.channel,
                    );
                  }}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>

                {whatsappChannel && renderImportButton(whatsApp)}
              </div>
            )}
          />
        )}
        {whatsappChannel && displayStatus === "OPENING" && (
          <div className={classes.sessionActions}>
            <Button
              className={`${classes.sessionButton} ${classes.sessionButtonDisabled}`}
              size="small"
              variant="outlined"
              disabled
              color="default"
            >
              {i18n.t("connections.buttons.connecting")}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    const displayStatus = getConnectionDisplayStatus(whatsApp);

    return (
      <div className={classes.customTableCell}>
        {displayStatus === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {displayStatus === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {displayStatus === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {displayStatus === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(displayStatus === "TIMEOUT" || displayStatus === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </div>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  const filteredWhatsApps = useMemo(() => {
    const normalizedSearch = searchParam.trim().toLowerCase();

    if (!normalizedSearch) {
      return whatsApps;
    }

    return whatsApps.filter((whatsApp) => {
      const haystack = [
        whatsApp?.name,
        whatsApp?.number,
        whatsApp?.channel,
        whatsApp?.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [searchParam, whatsApps]);

  const summary = useMemo(() => {
    const total = filteredWhatsApps.length;
    const connected = filteredWhatsApps.filter(
      (item) => getConnectionDisplayStatus(item) === "CONNECTED",
    ).length;
    const waitingQr = filteredWhatsApps.filter(isAwaitingWhatsAppQrCode).length;
    const unstable = filteredWhatsApps.filter((item) =>
      ["DISCONNECTED", "TIMEOUT", "PAIRING"].includes(
        getConnectionDisplayStatus(item),
      ),
    ).length;

    return {
      total,
      connected,
      waitingQr,
      unstable,
    };
  }, [filteredWhatsApps]);

  const summaryCards = useMemo(
    () => [
      { key: "connections", label: "Conexoes visiveis", value: summary.total },
      { key: "connected", label: "Conectadas", value: summary.connected },
      { key: "qr", label: "Aguardando QR", value: summary.waitingQr },
      { key: "alerts", label: "Com alerta", value: summary.unstable },
    ],
    [summary],
  );

  if (user.profile === "user" && user.allowConnections === "disabled") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>

      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}

      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      <MetaConnectionModal
        open={metaModalOpen}
        initialChannel={metaInitialChannel}
        reconnectConnectionId={metaReconnectConnectionId}
        permittedChannels={[
          ...(planConfig?.plan?.useFacebook ? ["facebook"] : []),
          ...(planConfig?.plan?.useInstagram ? ["instagram"] : []),
        ]}
        onClose={() => {
          setMetaModalOpen(false);
          setMetaReconnectConnectionId(null);
        }}
        onConnected={() => {
          toast.success("Ativos Meta conectados com sucesso.");
        }}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("connections.title")}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <PopupState variant="popover" popupId="connections-new-menu">
              {(popupState) => (
                <React.Fragment>
                  <Can
                    role={user.profile}
                    perform="connections-page:addConnection"
                    yes={() => (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          {...bindTrigger(popupState)}
                        >
                          {i18n.t("connections.newConnection")}
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem
                            disabled={!planConfig?.plan?.useWhatsapp}
                            onClick={() => {
                              handleOpenWhatsAppModal();
                              popupState.close();
                            }}
                          >
                            <WhatsApp
                              fontSize="small"
                              style={{ marginRight: 10, color: "#25D366" }}
                            />
                            WhatsApp
                          </MenuItem>
                          <MenuItem
                            disabled={!planConfig?.plan?.useFacebook}
                            onClick={() => {
                              handleOpenMetaModal("facebook");
                              popupState.close();
                            }}
                          >
                            <Facebook
                              fontSize="small"
                              style={{ marginRight: 10, color: "#3b5998" }}
                            />
                            Facebook
                          </MenuItem>
                          <MenuItem
                            disabled={!planConfig?.plan?.useInstagram}
                            onClick={() => {
                              handleOpenMetaModal("instagram");
                              popupState.close();
                            }}
                          >
                            <Instagram
                              fontSize="small"
                              style={{ marginRight: 10, color: "#e1306c" }}
                            />
                            Instagram
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  />
                </React.Fragment>
              )}
            </PopupState>

            <Button
              variant="outlined"
              color="primary"
              onClick={restartWhatsapps}
            >
              {i18n.t("connections.restartConnections")}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={() => openInNewTab(supportWhatsapp)}
              disabled={!supportWhatsapp}
            >
              {i18n.t("connections.callSupport")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip
          ariaLabel="Indicadores de conexoes"
          items={summaryCards}
        />

        {statusImport?.all ? (
          <Card className={classes.importCard}>
            <CardContent>
              <Typography component="h5" variant="h5">
                {statusImport?.this === -1
                  ? i18n.t("connections.buttons.preparing")
                  : i18n.t("connections.buttons.importing")}
              </Typography>
              {statusImport?.this === -1 ? (
                <Typography component="h6" variant="h6" align="center">
                  <CircularProgress size={24} />
                </Typography>
              ) : (
                <>
                  <Typography component="h6" variant="h6" align="center">
                    {`${i18n.t("connections.typography.processed")} ${statusImport?.this} ${i18n.t("connections.typography.in")} ${statusImport?.all} ${i18n.t("connections.typography.date")}: ${statusImport?.date} `}
                  </Typography>
                  <Typography align="center">
                    <CircularProgressWithLabel
                      style={{ margin: "auto" }}
                      value={(statusImport?.this / statusImport?.all) * 100}
                    />
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Conexoes operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder="Buscar por nome, numero, canal ou status"
                type="search"
                value={searchParam}
                onChange={(event) => setSearchParam(event.target.value)}
                inputProps={{ "aria-label": "Pesquisar conexoes" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.total} visiveis`} />
              <Chip size="small" label={`${summary.connected} conectadas`} />
            </Box>
          </Box>

          {loading ? (
            <Box p={2}>
              <TableRowSkeleton columns={7} />
            </Box>
          ) : isMobile ? (
            <Box className={classes.mobileSection}>
              <div className={classes.mobileCards}>
                {filteredWhatsApps.map((whatsApp) => {
                  const statusMeta = getStatusMeta(
                    getConnectionDisplayStatus(whatsApp),
                  );
                  return (
                    <Paper key={whatsApp.id} className={classes.mobileCard}>
                      <div className={classes.mobileCardHeader}>
                        <div>
                          <Box display="flex" alignItems="center" gridGap={8}>
                            {getChannelIcon(whatsApp.channel)}
                            <Typography
                              variant="body1"
                              className={classes.rowName}
                            >
                              {whatsApp.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {whatsApp.number && whatsApp.channel === "whatsapp"
                              ? formatSerializedId(whatsApp.number)
                              : whatsApp.number || "-"}
                          </Typography>
                        </div>

                        {canManageConnections && (
                          <RowActionsMenu
                            buttonTitle={`Acoes da conexao ${whatsApp.name}`}
                            actions={getConnectionRowActions(whatsApp)}
                          />
                        )}
                      </div>

                      <div className={classes.mobileCardBody}>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Status
                          </Typography>
                          <Chip
                            size="small"
                            label={statusMeta.label}
                            style={{
                              backgroundColor: statusMeta.backgroundColor,
                              color: statusMeta.color,
                            }}
                          />
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Sessao
                          </Typography>
                          <Box>{renderActionButtons(whatsApp)}</Box>
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Ultima atualizacao
                          </Typography>
                          <Typography variant="body2">
                            {format(
                              parseISO(whatsApp.updatedAt),
                              "dd/MM/yy HH:mm",
                            )}
                          </Typography>
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Padrao
                          </Typography>
                          {whatsApp.isDefault ? (
                            <CheckCircle style={{ color: green[500] }} />
                          ) : (
                            <Typography variant="body2">Nao</Typography>
                          )}
                        </div>
                      </div>
                    </Paper>
                  );
                })}
              </div>
            </Box>
          ) : (
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      Canal
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.name")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.number")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.status")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.session")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.lastUpdate")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      {i18n.t("connections.table.default")}
                    </TableCell>
                    {canManageConnections && (
                      <TableCell align="center" className={classes.actionsCell}>
                        {i18n.t("connections.table.actions")}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWhatsApps.map((whatsApp) => (
                    <TableRow key={whatsApp.id} hover>
                      <TableCell align="center">
                        {getChannelIcon(whatsApp.channel)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" className={classes.rowName}>
                          {whatsApp.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {whatsApp.number && whatsApp.channel === "whatsapp"
                          ? formatSerializedId(whatsApp.number)
                          : whatsApp.number}
                      </TableCell>
                      <TableCell align="center">
                        {renderStatusToolTips(whatsApp)}
                      </TableCell>
                      <TableCell align="center">
                        {renderActionButtons(whatsApp)}
                      </TableCell>
                      <TableCell align="center">
                        {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                      </TableCell>
                      <TableCell align="center">
                        {whatsApp.isDefault && (
                          <div className={classes.customTableCell}>
                            <CheckCircle style={{ color: green[500] }} />
                          </div>
                        )}
                      </TableCell>
                      {canManageConnections && (
                        <TableCell
                          align="center"
                          className={classes.actionsCell}
                        >
                          <RowActionsMenu
                            buttonTitle={`Acoes da conexao ${whatsApp.name}`}
                            actions={getConnectionRowActions(whatsApp)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && filteredWhatsApps.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhuma conexao encontrada</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre um novo canal para continuar.
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Connections;
